import React, { useEffect, useState } from 'react';
import { Input, Modal, Radio } from 'antd';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { PlusIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { ActionButton, RowIconButton, StatusBadge } from '../../components/ui';
import {
    apiFieldErrors,
    createGuardian,
    deleteGuardian,
    fetchGuardians,
    fetchLead,
    firstApiError,
    normalizeGuardianPhone,
    updateGuardian,
} from '../../services/leadGuardianService';

const EMPTY = {
    type: 'guardian',
    name: '',
    phone: '',
    relationship: '',
    address: '',
    notes: '',
};

export default function LeadGuardiansModal({ leadId, open, onClose, onChanged }) {
    const { t } = useTranslation();
    const [lead, setLead] = useState(null);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [editingId, setEditingId] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});
    const [formOpen, setFormOpen] = useState(false);

    const load = async () => {
        if (!leadId) return;
        setLoading(true);
        try {
            const [detailsResult, listResult] = await Promise.allSettled([
                fetchLead(leadId),
                fetchGuardians(leadId),
            ]);
            const details = detailsResult.status === 'fulfilled' ? detailsResult.value : null;
            const list = listResult.status === 'fulfilled' ? listResult.value : [];
            if (!details && listResult.status === 'rejected' && detailsResult.status === 'rejected') {
                throw detailsResult.reason;
            }
            setLead(details);
            setItems(list.length ? list : details?.guardians ?? []);
        } catch (error) {
            const code = error?.response?.status;
            if (code === 403) toast.error(t('callCenter.forbidden'));
            else toast.error(firstApiError(error) || t('callCenter.loadError'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open && leadId) {
            setForm(EMPTY);
            setEditingId(null);
            setFormOpen(false);
            setFieldErrors({});
            load();
        }
    }, [open, leadId]);

    const openCreate = () => {
        setEditingId(null);
        setForm(EMPTY);
        setFieldErrors({});
        setFormOpen(true);
    };

    const openEdit = (item) => {
        setEditingId(item.id);
        setForm({
            type: item.type || 'guardian',
            name: item.name || '',
            phone: item.phone || '',
            relationship: item.relationship || '',
            address: item.address || '',
            notes: item.notes || '',
        });
        setFieldErrors({});
        setFormOpen(true);
    };

    const buildBody = () => {
        const body = {
            type: form.type,
            name: form.name.trim(),
            phone: normalizeGuardianPhone(form.phone),
            relationship: form.relationship.trim(),
        };
        body.address = form.address.trim() ? form.address.trim() : null;
        body.notes = form.notes.trim() ? form.notes.trim() : null;
        return body;
    };

    const handleSave = async () => {
        const body = buildBody();
        const nextErrors = {};
        if (!body.name) nextErrors.name = t('callCenter.guardianNameRequired');
        if (!body.phone) nextErrors.phone = t('callCenter.guardianPhoneRequired');
        if (!body.relationship) nextErrors.relationship = t('callCenter.relationshipRequired');
        if (Object.keys(nextErrors).length) {
            setFieldErrors(nextErrors);
            return;
        }
        setSaving(true);
        setFieldErrors({});
        try {
            if (editingId) await updateGuardian(leadId, editingId, body);
            else await createGuardian(leadId, body);
            toast.success(editingId ? t('callCenter.guardianUpdated') : t('callCenter.guardianAdded'));
            setFormOpen(false);
            setForm(EMPTY);
            setEditingId(null);
            await load();
            onChanged?.();
        } catch (error) {
            setFieldErrors(apiFieldErrors(error));
            toast.error(firstApiError(error) || t('callCenter.guardianSaveError'));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = (item) => {
        Modal.confirm({
            title: t('callCenter.guardianDeleteConfirm'),
            okText: t('common.delete'),
            cancelText: t('common.cancel'),
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    await deleteGuardian(leadId, item.id);
                    toast.success(t('callCenter.guardianDeleted'));
                    await load();
                    onChanged?.();
                } catch (error) {
                    toast.error(firstApiError(error) || t('callCenter.guardianSaveError'));
                }
            },
        });
    };

    return (
        <Modal
            title={lead ? `${t('callCenter.guardians')} · ${lead.name}` : t('callCenter.guardians')}
            open={open}
            onCancel={onClose}
            footer={null}
            centered
            width="100%"
            style={{ maxWidth: 640 }}
            destroyOnClose
        >
            {lead && (
                <div className="mb-3 flex flex-wrap items-center gap-2 text-sm text-slate-600">
                    {lead.birth_at && <span>{t('callCenter.birthAt')}: {lead.birth_at}</span>}
                    {lead.age != null && <span>{t('callCenter.age')}: {lead.age}</span>}
                    {lead.is_minor === true && <StatusBadge tone="amber">{t('callCenter.minor')}</StatusBadge>}
                    {lead.is_minor === false && <StatusBadge tone="green">{t('callCenter.adult')}</StatusBadge>}
                </div>
            )}

            <div className="mb-3 flex justify-end">
                <ActionButton collapseOnMobile={false} icon={<PlusIcon className="h-4 w-4" />} onClick={openCreate}>
                    {t('callCenter.addGuardian')}
                </ActionButton>
            </div>

            {loading ? (
                <p className="text-sm text-slate-400">{t('common.loading')}</p>
            ) : items.length === 0 ? (
                <p className="text-sm text-slate-400">{t('callCenter.noGuardians')}</p>
            ) : (
                <div className="space-y-2">
                    {items.map((item) => (
                        <div key={item.id} className="rounded-lg border border-slate-200 px-3 py-2">
                            <div className="flex items-start gap-2">
                                <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="text-sm font-semibold text-slate-800">{item.name}</span>
                                        <StatusBadge tone={item.type === 'relative' ? 'violet' : 'brand'}>
                                            {item.type === 'relative' ? t('callCenter.relative') : t('callCenter.guardian')}
                                        </StatusBadge>
                                    </div>
                                    <p className="mb-0 mt-1 text-xs text-slate-500">
                                        {item.relationship} · {item.phone}
                                    </p>
                                    {item.address && <p className="mb-0 mt-0.5 text-xs text-slate-400">{item.address}</p>}
                                    {item.notes && <p className="mb-0 mt-0.5 text-xs text-slate-400">{item.notes}</p>}
                                </div>
                                <RowIconButton onClick={() => openEdit(item)} aria-label={t('common.edit')}>
                                    <PencilSquareIcon className="h-[18px] w-[18px]" />
                                </RowIconButton>
                                <RowIconButton onClick={() => handleDelete(item)} aria-label={t('common.delete')}>
                                    <TrashIcon className="h-[18px] w-[18px]" />
                                </RowIconButton>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {formOpen && (
                <div className="mt-4 space-y-3 rounded-xl border border-slate-200 bg-slate-50/70 p-3">
                    <Radio.Group
                        value={form.type}
                        onChange={(e) => setForm((prev) => ({ ...prev, type: e.target.value }))}
                    >
                        <Radio.Button value="guardian">{t('callCenter.guardian')}</Radio.Button>
                        <Radio.Button value="relative">{t('callCenter.relative')}</Radio.Button>
                    </Radio.Group>
                    <div>
                        <Input
                            placeholder={t('callCenter.guardianName')}
                            maxLength={255}
                            value={form.name}
                            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                        />
                        {fieldErrors.name && <p className="mt-1 mb-0 text-xs text-red-500">{fieldErrors.name}</p>}
                    </div>
                    <div>
                        <Input
                            placeholder={t('callCenter.guardianPhone')}
                            value={form.phone}
                            onChange={(e) =>
                                setForm((prev) => ({ ...prev, phone: normalizeGuardianPhone(e.target.value) }))
                            }
                        />
                        {fieldErrors.phone && <p className="mt-1 mb-0 text-xs text-red-500">{fieldErrors.phone}</p>}
                    </div>
                    <div>
                        <Input
                            placeholder={t('callCenter.relationship')}
                            maxLength={100}
                            value={form.relationship}
                            onChange={(e) => setForm((prev) => ({ ...prev, relationship: e.target.value }))}
                        />
                        {fieldErrors.relationship && (
                            <p className="mt-1 mb-0 text-xs text-red-500">{fieldErrors.relationship}</p>
                        )}
                    </div>
                    <Input
                        placeholder={t('callCenter.address')}
                        maxLength={255}
                        value={form.address}
                        onChange={(e) => setForm((prev) => ({ ...prev, address: e.target.value }))}
                    />
                    <Input.TextArea
                        rows={2}
                        maxLength={2000}
                        placeholder={t('callCenter.notes')}
                        value={form.notes}
                        onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                    />
                    <div className="flex justify-end gap-2">
                        <button
                            type="button"
                            className="text-sm text-slate-500"
                            onClick={() => {
                                setFormOpen(false);
                                setEditingId(null);
                            }}
                        >
                            {t('common.cancel')}
                        </button>
                        <ActionButton collapseOnMobile={false} loading={saving} onClick={handleSave}>
                            {t('common.save')}
                        </ActionButton>
                    </div>
                </div>
            )}
        </Modal>
    );
}
